import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminService, DeletionRequest } from '../services/admin.service';

@Component({
  selector: 'app-account-deletion-requests',
  templateUrl: './account-deletion-requests.component.html',
  styleUrls: ['./account-deletion-requests.component.css'],
  standalone: false
})
export class AccountDeletionRequestsComponent implements OnInit {
  displayedColumns: string[] = ['createdAt', 'userName', 'userEmail', 'reason', 'status', 'actions'];
  dataSource = new MatTableDataSource<DeletionRequest>();
  isLoading = true;
  statusFilter = new FormControl('all');
  processingRequest: string | null = null;
  
  statusOptions = [
    { value: 'all', label: 'Tous les statuts' },
    { value: 'pending', label: 'En attente' },
    { value: 'processing', label: 'En cours' },
    { value: 'completed', label: 'Complétées' },
    { value: 'rejected', label: 'Rejetées' }
  ];

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    public adminService: AdminService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Vérifier les droits d'administration
    if (!this.adminService.isAdmin()) {
      this.router.navigate(['/admin/login']);
      return;
    }
    
    this.loadDeletionRequests();
    
    // Configurer le filtre de statut
    this.statusFilter.valueChanges.subscribe(value => {
      this.applyFilter(value);
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  loadDeletionRequests(): void {
    this.isLoading = true;
    this.adminService.getDeletionRequests().subscribe(
      requests => {
        this.dataSource.data = requests;
        this.isLoading = false;
        console.log('Demandes chargées:', requests); // Debug log
      },
      error => {
        console.error('Erreur lors du chargement des demandes:', error);
        this.snackBar.open('Erreur lors du chargement des demandes', 'Fermer', { duration: 5000 });
        this.isLoading = false;
      }
    );
  }

  applyFilter(status: string): void {
    if (status === 'all') {
      this.dataSource.filter = '';
    } else {
      this.dataSource.filterPredicate = (data: DeletionRequest, filter: string) => {
        return data.status === filter;
      };
      this.dataSource.filter = status;
    }
  }

  getStatusLabel(status: string): string {
    const option = this.statusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  }

  async changeStatus(requestId: string, status: 'pending' | 'processing' | 'completed' | 'rejected'): Promise<void> {
    this.processingRequest = requestId;
    
    try {
      // Demander une confirmation pour les actions irréversibles
      if (status === 'completed') {
        const confirmed = confirm('Êtes-vous sûr de vouloir approuver cette demande ? Le compte utilisateur sera définitivement supprimé.');
        if (!confirmed) {
          this.processingRequest = null;
          return;
        }
      }
      
      let adminNote = '';
      if (status === 'rejected') {
        adminNote = prompt('Veuillez indiquer la raison du rejet:') || '';
      }
      
      await this.adminService.updateRequestStatus(requestId, status, adminNote);
      
      let message = '';
      switch (status) {
        case 'processing':
          message = 'Demande marquée comme en cours de traitement';
          break;
        case 'completed':
          message = 'Demande approuvée et compte supprimé avec succès';
          break;
        case 'rejected':
          message = 'Demande rejetée';
          break;
        default:
          message = 'Statut mis à jour';
      }
      
      this.snackBar.open(message, 'Fermer', { duration: 3000 });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      this.snackBar.open('Erreur lors de la mise à jour du statut', 'Fermer', { duration: 5000 });
    } finally {
      this.processingRequest = null;
    }
  }
  
  logout(): void {
    this.adminService.adminLogout().then(() => {
      this.router.navigate(['/admin/login']);
    });
  }

  navigateToDashboard(): void {
    this.router.navigate(['/admin/dashboard']);
  }
}
